import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlaneTakeoff, Radar } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { saveTripNav } from "@/components/TripSwipeNav";
import { GROUPS, MoveBadge, code, groupOf, lateText, nextLeg, shownStatus, zoned } from "@/components/TrackingBits";
import { fmtDate, isStaff } from "@/lib/format";
import { useMe } from "@/context/MeContext";
import { SwipeToDelete } from "@/components/SwipeToDelete";
import { FlightProgressMini } from "@/components/FlightProgress";

function BoardCard({ t, i }) {
  const navigate = useNavigate();
  const lg = nextLeg(t);
  const idx = t.legs.indexOf(lg);
  const status = t.status === "completed" ? "landed" : shownStatus(lg);
  const open = () => navigate(`/tracking/${t.id}`);
  const live = t.status !== "completed" && lg.track_url;
  return (
    <div role="link" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }} style={{ animationDelay: `${i * 50}ms` }}
      className="rise lux-card w-full text-left p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_auto] gap-4 sm:items-center hover:border-[#C6A55C]/60 transition-colors cursor-pointer"
      data-testid={`tracking-card-${t.id}`}>
      <div className="min-w-0">
        <p className="eyebrow mb-1">{t.trip_ref}</p>
        <p className="font-serif-display text-2xl text-white tracking-wide">{code(lg.from_airport)} <span className="text-[#C6A55C]">→</span> {code(lg.diverted_to || lg.to_airport)}</p>
        <p className="text-xs text-zinc-500 mt-1">Leg {idx + 1} of {t.legs.length} · {t.broker_name}</p>
      </div>
      <div>
        <p className="font-serif-display text-xl text-white" data-testid={`tracking-card-tail-${t.id}`}>{t.aircraft.tail_number || "Tail TBA"}</p>
        <p className="text-xs text-zinc-500">{t.aircraft.aircraft_type}</p>
      </div>
      <div className="flex sm:flex-col sm:items-end gap-2 items-center justify-between flex-wrap">
        <MoveBadge status={status} testId={`tracking-card-status-${t.id}`} />
        {t.status !== "completed" && lg.progress?.landing_overdue_min != null && (
          <span className="text-[10px] tracking-[0.14em] uppercase font-semibold px-2.5 py-1 rounded-full border bg-rose-50 text-rose-700 border-rose-300" data-testid={`tracking-card-overdue-${t.id}`}>
            Landing overdue · {lateText(lg.progress.landing_overdue_min)}
          </span>
        )}
        <p className="text-sm text-white whitespace-nowrap" data-testid={`tracking-card-time-${t.id}`}>{fmtDate(lg.date)} · {zoned(lg.departed_at || lg.delayed_to || lg.time, lg.progress?.dep_zone) || "Time TBA"}</p>
        {live && (
          <a href={lg.track_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
            className="frame-btn !py-2 !px-3.5 !border-[#C6A55C]/60" data-testid={`tracking-card-track-live-${t.id}`}>
            <Radar size={12} className="text-[#C6A55C]" /> Track live
          </a>
        )}
      </div>
      <FlightProgressMini lg={lg} testId={`tracking-card-progress-${t.id}`} />
    </div>
  );
}

export default function FlightTracking() {
  const api = useApi();
  const { me } = useMe();
  const staff = isStaff(me);
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api.get("/tracking").then((r) => { setItems(r.data); saveTripNav(r.data, "tracking"); }).catch((e) => toast.error(errMsg(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="flight-tracking-page">
      <PageHero testId="tracking-hero">
        <p className="eyebrow mb-3">Booked trips</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-white uppercase">Flight tracking</h1>
      </PageHero>
      {!items ? <div className="py-20 grid place-items-center"><div className="jet-loader" /></div> : !items.length ? (
        <div className="lux-card px-6 py-12 text-center text-sm text-zinc-500 mt-10" data-testid="tracking-empty">
          <PlaneTakeoff className="mx-auto mb-3 text-[#C6A55C]" size={22} />No tracked trips yet. Booked trips appear here once the flight team starts tracking them.
        </div>
      ) : GROUPS.map(([g, label]) => {
        const list = items.filter((t) => groupOf(t) === g);
        if (!list.length) return null;
        return (
          <section key={g} className="mt-12" data-testid={`tracking-group-${g}`}>
            <h2 className="eyebrow mb-4">{label} · {list.length}</h2>
            <div className="space-y-3">{list.map((t, i) => (
              <SwipeToDelete key={t.id} tripId={t.trip_id} tripRef={t.trip_ref} enabled={staff && g !== "air"} onDeleted={load} testId={`tracking-swipe-${t.id}`}>
                <BoardCard t={t} i={i} />
              </SwipeToDelete>
            ))}</div>
          </section>
        );
      })}
    </div>
  );
}
